import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, Scale, BookMarked, Landmark, Layers, MailCheck } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal } from "@/components/landing/Reveal";
import { STATUS_LABELS } from "@/data/apparitions";
import { scrollToId } from "@/lib/nav";
import { SCRIPTURE_EDITION_NOTICE, SCRIPTURE_EDITION_SCOPE, SITE_AFFILIATION, PERSONAL_TESTIMONY_POLICY } from "@/data/editorial";
import { CANADIAN_LECTIONARY_URL, SCRIPTURE_PERMISSIONS_URL } from "@/data/scripture";
import { ABOUT_AUTHOR } from "@/data/aboutMission";

const COMMITMENTS = [
  ["Faithful to Catholic teaching", "We use the Catechism, Scripture, Holy See documents, official diocesan and shrine materials, and established Catholic sources. Where the Church has not spoken, we say so."],
  ["Accurate about apparitions", "We never describe a case as simply “Vatican approved.” Every entry states the exact status and the authority that gave it: diocesan recognition, permission for devotion, nihil obstat, or another official judgement."],
  ["Clear distinction of sources", "Official Church documents, historical records, witness testimony, devotional tradition, and commentary are labelled separately on every page."],
  ["Pastoral, never sensational", `No fear-based prophecy, no conspiracy claims, no financial pressure and no promises of guaranteed healing. ${PERSONAL_TESTIMONY_POLICY}`],
  ["Accessible to everyone", "Plain English, mobile-first pages, a large-text mode, strong contrast, and captions or transcripts for any media."],
  ["Non-commercial at the beginning", "No intrusive advertising. If donations are ever needed, finances will be transparent, and we will never sell promises, blessings, or spiritual favours."],
];

const LABEL_EXPLAINERS = {
  diocesan: "A judgement by the competent local bishop. Its scope must be read in the entry: Lourdes (1862) recognizes the apparitions; Fátima (1930) declares the visions worthy of belief and permits public devotion; Kibeho (2001) recognizes only the three named initial Marian testimonies. Permission for a devotion is not automatically a declaration of supernatural origin.",
  holySee: "A related papal or dicastery document is available, such as a liturgical act, canonization, theological commentary or shrine designation. This is a source label, not an apparition verdict. Neither canonization of a visionary nor a papal visit authenticates every reported message.",
  nihil: "Under the 2024 Norms of the Dicastery for the Doctrine of the Faith, the bishop, with the Dicastery’s approval, may declare that nothing prevents the faithful from prudent adherence to a devotion and its message — without declaring its supernatural origin.",
  historical: "Established shrines and devotions whose founding accounts are distinguished from a modern decree on supernatural origin. Guadalupe’s liturgical honours and Juan Diego’s canonization are documented separately; Knock has its own commissions and later shrine designation. Age, popularity and papal honours do not replace a case-specific judgement.",
  discernment: "Cases still under examination by the competent authority, or not included in this collection because their status is unclear or negative. This label does not assert a favourable judgement.",
};

const NORMS = [
  ["Nihil obstat", "Signs of grace and spiritual fruits are recognized, with no particularly critical or risky aspects detected so far. The bishop may appreciate the pastoral value without declaring supernatural authenticity or requiring belief."],
  ["Prae oculis habeatur", "Positive signs are present, but some elements need clarification; careful discernment continues."],
  ["Curatur", "Critical elements exist, yet the phenomenon is widespread with verifiable fruits; the bishop is asked not to encourage it but to purify it."],
  ["Sub mandato", "The problems lie with a person or group misusing the phenomenon; the Holy See entrusts its pastoral direction to the bishop or a delegate."],
  ["Prohibetur et obstruatur", "Critical issues and risks outweigh positive elements; the faithful are told that adherence is not permitted."],
  ["Declaratio de non supernaturalitate", "The bishop, with the Dicastery, declares that the phenomenon is not supernatural, on the basis of concrete evidence."],
];

const HIERARCHY = [
  "Holy See, Vatican, and Dicastery documents",
  "The relevant diocese, bishops’ conference, or official shrine",
  "The original decree, historical archive, or scholarly publication",
  "The Catechism, Scripture, and official liturgical texts",
  "Trusted explanatory Catholic sources such as EWTN and Ascension Press",
  "Other devotional material — only if clearly identified and appropriately sourced",
];

const SECTIONS = [
  { id: "who", n: "01", label: "Who we are", icon: ShieldCheck },
  { id: "commitments", n: "02", label: "Editorial commitments", icon: Scale },
  { id: "discernment", n: "03", label: "How the Church discerns", icon: Landmark },
  { id: "sources", n: "04", label: "Source hierarchy", icon: Layers },
  { id: "disclaimer", n: "05", label: "Essential disclaimer", icon: BookMarked },
  { id: "corrections", n: "06", label: "Review & corrections", icon: MailCheck },
];

const Chapter = ({ id, n, title, children }) => (
  <section id={id} className="scroll-mt-28 border-t border-marian/15 py-14 first:border-t-0 first:pt-0" data-testid={`standards-${id}`}>
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Chapter {n}</p>
      <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">{title}</h2>
      <div className="mt-7 space-y-5 text-base leading-relaxed text-charcoal/90 sm:text-lg">{children}</div>
    </Reveal>
  </section>
);

export default function EditorialStandards() {
  const { hash } = useLocation();
  const [active, setActive] = useState("who");

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (hash) setTimeout(() => scrollToId(hash.slice(1)), 150);
  }, [hash]);

  return (
    <main data-testid="editorial-standards-page">
      <Seo
        title="Editorial Standards — sourcing and discernment policy · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="How Theotokos · Mater Dei sources its content, labels the Church status of Marian apparitions, and distinguishes official documents from testimony and commentary."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "About", to: "/about" }, { label: "Editorial Standards" }]}
        eyebrow="Our charter"
        lines={["Editorial", "Standards"]}
        aside={
          <div className="flex items-center gap-4 rounded-2xl border border-marian/15 bg-white/70 p-5">
            <img src="/emblem-512.png" alt="Emblem of Theotokos · Mater Dei" className="h-20 w-20 object-contain" />
            <p className="text-xs leading-relaxed text-slateblue">
              The emblem unites West and East: the Latin monogram M within a Byzantine mandorla,
              the three-bar cross, the lily and the rose, beneath twelve stars.
            </p>
          </div>
        }
      >
        Everything on this site is bound by six commitments, a strict vocabulary for the
        Church’s judgements on apparitions, and a fixed hierarchy of sources. This page is the
        linked explanation behind every status label you will meet.
      </PageIntro>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-8 lg:grid-cols-[15rem_minmax(0,1fr)] lg:px-16">
        <nav aria-label="Chapters" className="hidden lg:block">
          <ol className="sticky top-32 space-y-1">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  data-testid={`toc-${s.id}`}
                  onClick={() => scrollToId(s.id)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    active === s.id ? "bg-paleblue text-midnight" : "text-slateblue hover:text-marian"
                  }`}
                >
                  <span className="font-mono text-[10px] text-gold">{s.n}</span>
                  {s.label}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="min-w-0 max-w-3xl">
          <Chapter id="who" n="01" title="Who we are">
            <p data-testid="standards-ministry-affiliation">
              Theotokos Mater Dei is a work of <a href={ABOUT_AUTHOR.ministryUrl} target="_blank" rel="noopener noreferrer" data-testid="standards-ministry-link" className="font-semibold text-marian underline decoration-gold/60 underline-offset-4 transition-colors hover:text-midnight focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">{ABOUT_AUTHOR.ministry}</a>.
            </p>
            <p data-testid="standards-who-summary">
              Its creator, Fred Bachour, is a former seminarian
              who, after eight years of seminary formation, now lives his vocation as a faithful lay
              Melkite Catholic. Raised in the Latin tradition, he came to cherish the distinct spiritual
              riches of East and West. Inspired by St. Carlo Acutis’s use of technology for evangelization,
              St. John Paul II’s vision of the Church breathing with both lungs, and St. Louis de Montfort’s
              Christ-centred Marian devotion, this work gathers prayers, teachings, and sacred traditions
              to help others draw nearer to Jesus through his Mother.
            </p>
            <p data-testid="standards-who-independence">
              This is a lay offering of study, prayer, and charitable defense of Catholic truth, grounded
              in Scripture, Tradition, and the Church’s teaching authority. It is not an official website
              of the Holy See, a diocese, a shrine, or a religious institute, and its founder does not
              speak as the Church’s teaching authority. Careful sources and a clear distinction between
              official texts and original reflections remain essential to the mission.
            </p>
            <Link to="/about#mission" data-testid="standards-about-testimony-link" className="inline-flex text-sm font-semibold text-marian underline decoration-gold underline-offset-4 transition-colors hover:text-midnight">Read the full testimony · Why we exist</Link>
          </Chapter>

          <Chapter id="commitments" n="02" title="Six editorial commitments">
            <ol className="space-y-6">
              {COMMITMENTS.map(([title, text], i) => (
                <li key={title} data-testid={`standards-commitment-${i + 1}`} className="grid gap-3 sm:grid-cols-[3rem_1fr]">
                  <span className="font-serif text-3xl font-semibold text-gold/80">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-2xl font-semibold text-midnight">{title}</h3>
                    <p data-testid={i === 3 ? "standards-personal-testimony-policy" : `standards-commitment-text-${i + 1}`} className="mt-2 text-base leading-relaxed">{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Chapter>

          <Chapter id="discernment" n="03" title="How the Church discerns apparitions">
            <p data-testid="standards-private-revelation-teaching">
              Private revelations, even when recognized, do not belong to the deposit of faith. Their
              role, the Catechism teaches, is “to help live more fully by it in a certain period of
              history” (<a href="https://www.vatican.va/archive/ENG0015/__PH.HTM" target="_blank" rel="noopener noreferrer" data-testid="standards-catechism-67-source" className="text-marian underline underline-offset-4 transition-colors hover:text-gold">Catechism of the Catholic Church, §67 · excerpt</a>). Catholics are never obliged to believe them. The Church’s task is to
              judge whether a devotion is safe, fruitful, and free of error — and she does so with a
              precise vocabulary. We use that vocabulary and never a generic “approved.”
            </p>
            <h3 className="pt-4 font-serif text-2xl font-semibold text-midnight">Our five status labels</h3>
            <dl className="space-y-4">
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <div key={k} id={`label-${k}`} className="rounded-2xl border border-marian/15 bg-white/80 p-5" data-testid={`label-${k}`}>
                  <dt className="flex items-center gap-2 font-semibold text-midnight">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" /> {v}
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-charcoal/85">{LABEL_EXPLAINERS[k]}</dd>
                </div>
              ))}
            </dl>
            <h3 className="pt-4 font-serif text-2xl font-semibold text-midnight">The 2024 Norms of the Dicastery for the Doctrine of the Faith</h3>
            <p>
              On 17 May 2024 the Dicastery published new Norms for Proceeding in the Discernment of
              Alleged Supernatural Phenomena. As a rule, neither the bishop nor the Holy See now
              declares a phenomenon to be of supernatural origin; the bishop, with the Dicastery’s
              approval, reaches one of six conclusions:
            </p>
            <ol className="grid gap-3 sm:grid-cols-2">
              {NORMS.map(([t, d], i) => (
                <li key={t} className="rounded-xl border border-marian/10 bg-paleblue/50 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-marian">{i + 1}</p>
                  <p className="mt-1 font-serif text-lg font-semibold italic text-midnight">{t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-charcoal/85">{d}</p>
                </li>
              ))}
            </ol>
            <p className="text-sm text-slateblue">
              Source:{" "}
              <a
                href="https://www.vatican.va/roman_curia/congregations/cfaith/documents/rc_ddf_doc_20240517_norme-fenomeni-soprannaturali_en.html"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-marian underline-offset-2 hover:underline"
                data-testid="norms-2024-link"
              >
                Dicastery for the Doctrine of the Faith, Norms (2024)
              </a>
              . The new norms do not retrospectively rename older judgements as nihil obstat. Earlier decisions retain their particular scope unless the competent authority revises them.
            </p>
          </Chapter>

          <Chapter id="sources" n="04" title="Suggested source hierarchy">
            <div id="scripture" data-testid="standards-scripture-policy" className="scroll-mt-32 space-y-4 border-b border-marian/15 pb-6">
              <h3 className="font-serif text-lg font-semibold text-midnight">Scripture editions & quotations</h3>
              <p data-testid="standards-scripture-disclaimer" className="text-sm leading-relaxed">{SCRIPTURE_EDITION_NOTICE}</p>
              <p data-testid="standards-scripture-scope" className="text-sm leading-relaxed">{SCRIPTURE_EDITION_SCOPE}</p>
              <p data-testid="standards-quotation-policy" className="text-sm leading-relaxed">Direct quotations identify their author, work, passage or date, and the linked edition wherever available. Ellipses mark omissions. Editorial summaries appear without enclosing quotation marks. Reported apparition words are testimony in the cited account, not independently recorded speech. When wording or attribution cannot be verified, we use an explicitly labelled reflection rather than invent a quotation.</p>
              <p data-testid="standards-prayer-policy" className="mt-4 text-sm leading-relaxed">Prayer texts are not composed or modernized for this website. We use cited historical texts, original-language prayers, and identified publisher editions. The full Montfort consecration follows F. W. Faber’s 1863 translation; Scripture selections use RSV-CE. Historical texts and musical settings keep their attributed original wording and are not relabelled as RSV-CE. Editorial reading plans and explanations are distinct from prayer texts. Historical indulgence notices in source books are not presented as current Church grants.</p>
              <div className="mt-4 flex flex-wrap gap-5 text-xs"><a href={CANADIAN_LECTIONARY_URL} target="_blank" rel="noreferrer" data-testid="standards-canadian-lectionary-source" className="text-marian underline">CCCB · Canadian lectionary translation</a><a href={SCRIPTURE_PERMISSIONS_URL} target="_blank" rel="noreferrer" data-testid="standards-scripture-permissions" className="text-marian underline">Publisher’s quotation guidelines</a></div>
              <p data-testid="standards-audio-policy" className="mt-4 text-sm leading-relaxed">Prayer recordings require a verified recording licence, performer attribution and a match to the named prayer. Language and excerpt scope are identified. No AI-generated speech or unrelated music is substituted. Prayers without a sourced recording are marked “Recording not yet available”.</p>
              <p data-testid="standards-editorial-review-date" className="text-xs text-slateblue">Quotation and citation review · 9 October 2026. This editorial review is not ecclesiastical approval of the website.</p>
            </div>
            <p>When sources disagree, the higher authority governs. Every entry cites at least one source from the first two tiers.</p>
            <ol className="space-y-2">
              {HIERARCHY.map((h, i) => (
                <li key={h} className="flex items-start gap-4 rounded-xl border border-marian/10 bg-white/80 px-5 py-3.5">
                  <span className="font-serif text-2xl font-semibold text-gold/80">{i + 1}</span>
                  <span className="pt-1 text-base">{h}</span>
                </li>
              ))}
            </ol>
            <p className="text-sm text-slateblue">
              Images are reproduced from public-domain and Creative Commons sources with attribution on
              every page. Shrine photography that is not freely licensed is replaced by illustrative
              imagery, clearly labelled.
            </p>
          </Chapter>

          <Chapter id="disclaimer" n="05" title="Essential disclaimer">
            <div className="rounded-2xl bg-midnight p-8 font-serif text-xl italic leading-relaxed text-ivory sm:text-2xl" data-testid="long-disclaimer">
              {SITE_AFFILIATION} It is not an official website of the
              Holy See, a diocese, or a shrine unless a page expressly says otherwise. Material concerning
              apparitions and private revelations is presented for education and devotion. Catholics are
              not obliged to believe private revelations, even when the Church permits or encourages
              devotion.
            </div>
            <p>
              A short form of this disclaimer appears in the footer of every page. Nothing here replaces
              the guidance of your pastor, your bishop, or the teaching of the Church.
            </p>
          </Chapter>

          <Chapter id="corrections" n="06" title="Review and corrections">
            <p>
              Every entry shows its last review date. Church status, dates, and sources are re-checked
              against the shrine and diocese at least once a year and whenever the competent authority
              issues a new judgement.
            </p>
            <p>
              If you find an error — a misstated status, a wrong date, a broken source — please tell us.
              Corrections are made promptly and noted on the page.
            </p>
            <Link
              to="/contact"
              data-testid="standards-contact-link"
              className="inline-flex items-center gap-2 rounded-full bg-marian px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-midnight"
            >
              Send a correction
            </Link>
          </Chapter>
        </div>
      </div>
    </main>
  );
}
